import type { Theme, ThemeOptions } from '@material-ui/core'
import { createTheme as createMuiTheme } from '@material-ui/core/styles'
import type { ActivityType, QuoteStatus } from '../types'
import merge from 'lodash/merge'
import { secondaryPalette, brandPalette, semanticPalette } from './palette'
import type { BrandPalette, SecondaryPalette, SemanticPalette } from './palette'
import responsiveFontSizes from './responsiveFontSizes'

const PCT_50 = '50%'
const PCT_25 = '25%'
const PCT_75 = '75%'
const PCT_16_666667 = '16.666667%'
const PCT_33_333333 = '33.333333%'
const PCT_66_666667 = '66.666667%'
const PCT_83_333333 = '83.333333%'

export function createTheme(options?: ThemeOptions): Theme {
  const { fontScale = 1 } = options?.paintscout ?? {}

  const fontSizes = {
    h1: 2.5,
    h2: 1.5,
    h3: 1.25,
    h4: 1,
    h5: 1, // subtitle1
    h6: 1, // subtitle1
    subtitle1: 0.875,
    subtitle2: 0.875, // subtitle1
    body1: 1,
    body2: 0.875, // body1
    button: 1,
    caption: 0.875,
    overline: 0.875
  }

  Object.keys(fontSizes).forEach((size: string) => {
    fontSizes[size] = `${fontSizes[size] * fontScale}rem`
  })

  const lineHeight = 1.4
  // const letterSpacing = '0.137em'
  const fontWeight = 400
  const fontWeightMedium = 500
  const fontWeightBold = 600

  // ENG-2094: all body text, values and titles are P35.
  const blackText = brandPalette.Neutral.P35
  const greyText = brandPalette.Neutral.P75
  const whiteText = brandPalette.Neutral.White

  const statusColors = {
    draft: semanticPalette.Reduced.S70,
    sent: semanticPalette.Warning.S80,
    onHold: semanticPalette.Reduced.S70,
    // ENG-2094 status colours. Neither has a palette token yet; both sit a shade off Info.S60 / Success.S60.
    viewed: '#0889c8',
    accepted: '#0e9947',
    disputed: semanticPalette.Danger.S60,
    declined: semanticPalette.Danger.S60,
    invoiced: secondaryPalette['170'].C70,
    partial: secondaryPalette['320'].C60,
    paid: semanticPalette.Success.S60,
    overdue: semanticPalette.Danger.S60
  }

  const severityColors = {
    warning: {
      main: semanticPalette.Warning.S80,
      light: semanticPalette.Warning.S95,
      dark: semanticPalette.Warning.S40,
      contrastText: whiteText
    },
    success: {
      main: semanticPalette.Success.S60,
      light: semanticPalette.Success.S95,
      dark: semanticPalette.Success.S40,
      contrastText: whiteText
    },
    error: {
      main: semanticPalette.Danger.S60,
      light: semanticPalette.Danger.S95,
      dark: semanticPalette.Danger.S40,
      contrastText: whiteText
    },
    info: {
      main: semanticPalette.Info.S70,
      light: semanticPalette.Info.S95,
      dark: semanticPalette.Info.S40,
      contrastText: whiteText
    }
  }

  const breakPointsAdjuster = 190

  const theme = createMuiTheme(
    merge(
      {
        props: {
          MuiDialog: {
            disableScrollLock: true
          },
          MuiPopover: {
            disableScrollLock: true
          }
        },
        overrides: {
          MuiCssBaseline: {
            '@global': {
              html: {
                backgroundColor: brandPalette.Neutral.P98,
                // Dampen the document rubber-band so a `position: fixed` bottom bar
                // isn't dragged by the overscroll bounce (#21177). `contain` only
                // stops scroll-chaining (keeps it gentler than `none`, preserving
                // pull-to-refresh). Safari historically ignores overscroll-behavior
                // (see QuoteSidebar.tsx), so this is reinforcement — the real fix
                // lives in BulkActionBar's overscroll-aware repositioning.
                overscrollBehaviorY: 'contain',
                // Only reserve scrollbar gutter on devices with a fine pointer (desktop).
                // Mobile browsers use overlay scrollbars so no gutter is needed, and forcing
                // it can cause layout gaps on Chrome for Android.
                '@media (pointer: fine)': {
                  scrollbarGutter: 'stable'
                }
              },
              button: {
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                // Safari mobile: remove the 300ms double-tap-zoom delay so the
                // first tap fires a click. Without this, taps inside a dialog
                // while another input is focused are eaten dismissing the
                // virtual keyboard. See #20209 follow-up in DealForm.tsx.
                touchAction: 'manipulation'
              },
              strong: {
                fontWeight: fontWeightBold
              },
              body: {
                backgroundColor: brandPalette.Neutral.P98
              },
              '@media print': {
                html: { backgroundColor: '#fff' },
                body: { backgroundColor: '#fff' }
              }
            }
          },
          MuiMenuItem: {
            root: {
              paddingTop: 12,
              paddingLeft: 12,
              paddingRight: 12,
              paddingBottom: 12,
              color: blackText
            }
          },
          MuiPaper: {
            root: {
              boxShadow: 'none !important',
              // Card and sheet outline (ENG-2094). Rules inside a card use theme.palette.divider (P90).
              border: `1px solid ${brandPalette.Neutral.P85}`
            },
            elevation0: {
              border: 'none'
            }
          }
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600 + breakPointsAdjuster,
            md: 768 + breakPointsAdjuster,
            lg: 1024 + breakPointsAdjuster,
            xl: 1280 + breakPointsAdjuster
          }
        },
        size: {
          '0': '0px',
          px: '1px',
          '0.5': '0.125rem',
          '1': '0.25rem',
          '1.5': '0.375rem',
          '2': '0.5rem',
          '2.5': '0.625rem',
          '3': '0.75rem',
          '3.5': '0.875rem',
          '4': '1rem',
          '5': '1.25rem',
          '6': '1.5rem',
          '7': '1.75rem',
          '8': '2rem',
          '9': '2.25rem',
          '10': '2.5rem',
          '11': '2.75rem',
          '12': '3rem',
          '14': '3.5rem',
          '16': '4rem',
          '20': '5rem',
          '24': '6rem',
          '28': '7rem',
          '32': '8rem',
          '36': '9rem',
          '40': '10rem',
          '44': '11rem',
          '48': '12rem',
          '52': '13rem',
          '56': '14rem',
          '60': '15rem',
          '64': '16rem',
          '72': '18rem',
          '80': '20rem',
          '96': '24rem',
          '112': '32rem',
          '128': '48rem',
          '144': '64rem',

          auto: 'auto',
          '1/2': PCT_50,
          '1/3': PCT_33_333333,
          '2/3': PCT_66_666667,
          '1/4': PCT_25,
          '2/4': PCT_50,
          '3/4': PCT_75,
          '1/5': '20%',
          '2/5': '40%',
          '3/5': '60%',
          '4/5': '80%',
          '1/6': PCT_16_666667,
          '2/6': PCT_33_333333,
          '3/6': PCT_50,
          '4/6': PCT_66_666667,
          '5/6': PCT_83_333333,
          '1/12': '8.333333%',
          '2/12': PCT_16_666667,
          '3/12': PCT_25,
          '4/12': PCT_33_333333,
          '5/12': '41.666667%',
          '6/12': PCT_50,
          '7/12': '58.333333%',
          '8/12': PCT_66_666667,
          '9/12': PCT_75,
          '10/12': PCT_83_333333,
          '11/12': '91.666667%',
          full: '100%',
          min: 'min-content',
          max: 'max-content'
        },
        borderRadius: {
          none: '0',
          sm: '0.125rem',
          md: '0.25rem',
          lg: '0.5rem',
          xl: '0.75rem',
          '2xl': '1rem',
          '3xl': '1.5rem',
          full: '9999px'
        },
        boxShadow: {
          1: '3px 0px 12px -2px rgba(0,0,0,0.23)'
        },
        paintscout: {
          header: {
            height: {
              sm: '50px',
              md: '60px',
              lg: '90px'
            }
          },
          fontScale: 1,
          palette: {
            primary: brandPalette.Blue.Blue55,
            dark: brandPalette.Blue.Blue50,
            light: brandPalette.Blue.Blue80,
            secondary: {
              ...secondaryPalette
            },
            brand: {
              ...brandPalette
            },
            semantic: {
              ...semanticPalette
            }
          }
        },
        typography: {
          htmlFontSize: 16,
          fontWeightMedium,
          fontFamily: 'Inter, Arial, Helvetica, sans-serif',
          h1: {
            fontSize: fontSizes.h1,
            color: blackText,
            fontWeight: fontWeightBold,
            lineHeight
          },
          h2: {
            fontSize: fontSizes.h2,
            color: blackText,
            fontWeight: fontWeightMedium,
            lineHeight
          },
          h3: {
            fontSize: fontSizes.h3,
            color: blackText,
            fontWeight: fontWeightMedium,
            lineHeight
          },
          h4: {
            fontSize: fontSizes.h4,
            color: blackText,
            lineHeight,
            fontWeight: fontWeightMedium
          },
          h5: {
            fontSize: fontSizes.subtitle1,
            color: greyText,
            lineHeight,
            fontWeight: fontWeightBold
          },
          h6: {
            // same as subtitle1
            fontSize: fontSizes.subtitle1,
            color: greyText,
            lineHeight,
            fontWeight
          },
          subtitle1: {
            fontSize: fontSizes.subtitle1,
            color: greyText,
            lineHeight,
            fontWeight
          },
          subtitle2: {
            // same as subtitle1
            fontSize: fontSizes.subtitle1,
            color: greyText,
            lineHeight,
            fontWeight
          },
          body1: {
            fontSize: fontSizes.body1,
            lineHeight,
            fontWeight
          },
          body2: {
            // same as body1
            fontSize: fontSizes.body1,
            lineHeight,
            fontWeight
          },
          button: {
            fontFamily: 'Euclid, Arial, Helvetica, sans-serif',
            fontSize: fontSizes.button,
            lineHeight,
            fontWeight: fontWeightMedium,
            textTransform: 'unset' as const
          },
          caption: {
            fontSize: fontSizes.caption,
            lineHeight,
            fontWeight
          },
          overline: {
            fontSize: fontSizes.overline,
            lineHeight,
            fontWeight,
            textTransform: 'unset' as const
          }
        },
        palette: {
          // Default only: get-theme and admin pass their own primary, which wins the merge. A bare
          // createTheme() (previews, Storybook) used to fall through to MUI's indigo, which the
          // Checkbox now picks up for its checked state (ENG-2094). No `dark` here, so MUI still
          // derives one from whatever primary a caller passes.
          primary: { main: brandPalette.Blue.Blue55 },
          quote: {
            status: statusColors
          },
          presentation: {
            sectionBorder: {
              hover: secondaryPalette['290'].C60,
              focus: secondaryPalette['230'].C60
            },
            pageBorder: {
              hover: secondaryPalette['200'].C60
            }
          },
          ...severityColors,
          activityType: {
            note: statusColors.draft,
            created: statusColors.draft,
            edited: statusColors.draft,
            'conflict-resolution': statusColors.draft,
            'status-changed': statusColors.draft,
            'status-updated': statusColors.draft,
            archived: statusColors.draft,
            unarchived: statusColors.draft,
            copied: statusColors.draft,
            deleted: statusColors.draft,
            ...statusColors,
            payment: '#8A63A4',
            assignment: statusColors.sent,
            error: statusColors.declined,
            warning: statusColors.sent,
            downloaded: statusColors.draft,
            merged: statusColors.draft,
            'owner-changed': statusColors.draft,
            'type-changed': statusColors.draft,
            message: '#50A0CF',
            'payment-requested': statusColors.sent,
            'settings-edited': brandPalette.Neutral.P60
          },
          text: {
            primary: blackText
          },
          grey: {
            50: brandPalette.Neutral.P98,
            75: brandPalette.Neutral.P98, // same as 50
            100: brandPalette.Neutral.P96,
            200: brandPalette.Neutral.P94,
            300: brandPalette.Neutral.P90,
            400: brandPalette.Neutral.P80,
            500: brandPalette.Neutral.P70,
            600: brandPalette.Neutral.P60,
            700: brandPalette.Neutral.P50,
            800: brandPalette.Neutral.P35,
            900: brandPalette.Neutral.P30,
            A50: brandPalette.Neutral.P94, // same as 200
            A75: brandPalette.Neutral.P96, // same as 100
            A100: brandPalette.Neutral.P85,
            A200: brandPalette.Neutral.P75,
            A400: brandPalette.Neutral.P40,
            A700: brandPalette.Neutral.P50 // same as 700
          },
          divider: brandPalette.Neutral.P90
        }
      },
      options
    )
  )
  const withFontSizes = fontScale < 1 ? theme : responsiveFontSizes(theme)

  return withFontSizes
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    boxShadow?: {
      1?: string
    }
    size: {
      '0': '0px'
      px: '1px'
      '0.5': '0.125rem'
      '1': '0.25rem'
      '1.5': '0.375rem'
      '2': '0.5rem'
      '2.5': '0.625rem'
      '3': '0.75rem'
      '3.5': '0.875rem'
      '4': '1rem'
      '5': '1.25rem'
      '6': '1.5rem'
      '7': '1.75rem'
      '8': '2rem'
      '9': '2.25rem'
      '10': '2.5rem'
      '11': '2.75rem'
      '12': '3rem'
      '14': '3.5rem'
      '16': '4rem'
      '20': '5rem'
      '24': '6rem'
      '28': '7rem'
      '32': '8rem'
      '36': '9rem'
      '40': '10rem'
      '44': '11rem'
      '48': '12rem'
      '52': '13rem'
      '56': '14rem'
      '60': '15rem'
      '64': '16rem'
      '72': '18rem'
      '80': '20rem'
      '96': '24rem'
      '112': '32rem'
      '128': '48rem'
      '144': '64rem'
      auto: 'auto'
      '1/2': '50%'
      '1/3': '33.333333%'
      '2/3': '66.666667%'
      '1/4': '25%'
      '2/4': '50%'
      '3/4': '75%'
      '1/5': '20%'
      '2/5': '40%'
      '3/5': '60%'
      '4/5': '80%'
      '1/6': '16.666667%'
      '2/6': '33.333333%'
      '3/6': '50%'
      '4/6': '66.666667%'
      '5/6': '83.333333%'
      '1/12': '8.333333%'
      '2/12': '16.666667%'
      '3/12': '25%'
      '4/12': '33.333333%'
      '5/12': '41.666667%'
      '6/12': '50%'
      '7/12': '58.333333%'
      '8/12': '66.666667%'
      '9/12': '75%'
      '10/12': '83.333333%'
      '11/12': '91.666667%'
      full: '100%'
      min: 'min-content'
      max: 'max-content'
    }
    borderRadius: {
      none: '0'
      sm: '0.125rem'
      md: '0.25rem'
      lg: '0.5rem'
      xl: '0.75rem'
      '2xl': '1rem'
      '3xl': '1.5rem'
      full: '9999px'
    }
    paintscout: {
      header: {
        height: {
          sm: string
          md: string
          lg?: string
        }
      }
      fontScale: number
      palette: {
        primary: string
        dark: string
        light: string
        lighter: string
        secondary: SecondaryPalette
        brand: BrandPalette
        semantic: SemanticPalette
      }
    }
  }

  interface ThemeOptions {
    boxShadow?: {
      1?: string
    }
    size?: Theme['size']
    borderRadius?: Theme['borderRadius']
    paintscout?: {
      header?: {
        height?: {
          sm?: string
          md?: string
          lg?: string
        }
      }
      fontScale: number
      palette?: {
        primary?: string
        brand?: BrandPalette
      }
    }
  }
}

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    quote: {
      status: { [key in QuoteStatus]: string }
    }
    activityType: Record<ActivityType, string>
    status: { [key in QuoteStatus]: string }
    accentBackground?: {
      default?: string
      paper?: string
    }
    presentation: {
      sectionBorder: {
        focus: string
        hover: string
      }
      pageBorder: {
        hover: string
      }
    }
    onboarding: {
      main: string
    }
  }

  interface PaletteOptions {
    activityType?: Record<ActivityType, string>
    accentBackground?: Partial<TypeBackground>
    presentation?: {
      sectionBorder?: {
        focus?: string
        hover?: string
      }
      pageBorder?: {
        hover?: string
      }
    }
    onboarding?: {
      main?: string
    }
  }
}

declare module '@material-ui/styles/defaultTheme' {
  // makeStyles refers to DefaultTheme, so make sure we update that as well
  interface DefaultTheme extends Theme {}
}
